// Ported from specter-1 ui/packages/app/src/screens/Research.tsx:34-84.
// Handles three term shapes:
//   "Expansion (ACR)"   → two patterns, one per part
//   "Foo(bar)"          → one pattern for the prefix
//   plain term           → one pattern
// Short / uppercase tokens get word boundaries so "Tier" doesn't shadow "Tier 1".

export function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Common English stopwords whose lowercase shape collides with plausible
// acronyms. When Haiku proposes "OF" → optical fiber, the bare regex
// /\bOF\b/i matches every preposition "of" in the doc. The rule below
// drops the `i` flag for acronym-shaped terms (short + all-uppercase)
// whose lowercase form lives in this set — so "OF" matches only "OF",
// while well-known acronyms whose lowercase is NOT a stopword (API, REST,
// SQL, JSON, GPU, RAG, AI, ML, …) keep case-insensitive matching and so
// still pick up "REST api" mid-sentence.
//
// Source: ~30 most common English short words. NOT the full NLTK list
// (~127) because longer entries like "between" can't be acronym-shaped
// under our `length <= 4` gate and including them would be dead code.
const ACRONYM_STOPWORDS: ReadonlySet<string> = new Set([
  // 2-letter
  "am", "an", "as", "at", "be", "by", "do", "go", "he", "if",
  "in", "is", "it", "me", "my", "no", "of", "on", "or", "so",
  "to", "up", "us", "we",
  // 3-letter
  "all", "and", "any", "are", "but", "can", "did", "due", "few", "for",
  "get", "had", "has", "her", "him", "his", "how", "its", "may", "new",
  "not", "now", "off", "our", "out", "own", "see", "set", "the", "too",
  "two", "use", "was", "way", "who", "why", "yes", "you",
  // 4-letter
  "also", "been", "from", "have", "into", "just", "like", "more",
  "much", "only", "over", "same", "some", "such", "than", "that",
  "then", "this", "very", "well", "were", "what", "when", "with",
  "your",
]);

export function termPatterns(term: string): RegExp[] {
  const out: RegExp[] = [];
  const parts: string[] = [];
  const m = /^(.+?)\s*\((.+)\)\s*$/.exec(term);
  if (m) {
    parts.push(m[1]!.trim());
    const inside = m[2]!.trim();
    if (/[A-Za-z]{3,}/.test(inside)) parts.push(inside);
  } else if (/[A-Za-z](.*?)\(/.test(term)) {
    const idx = term.indexOf("(");
    parts.push(term.slice(0, idx).trim());
  } else {
    parts.push(term);
  }
  for (const part of parts) {
    if (!part) continue;
    const wordy = /^[A-Za-z][A-Za-z0-9-]*$/.test(part);
    const short = part.length <= 4;
    const upper = part === part.toUpperCase();
    // Stopword-shaped acronyms (term="OF", lowercase ∈ ACRONYM_STOPWORDS):
    // drop the `i` flag so we only match the actual uppercase token, never
    // the common-English-word collision. See ACRONYM_STOPWORDS docstring.
    const stopwordAcronym = wordy && upper && short && ACRONYM_STOPWORDS.has(part.toLowerCase());
    if (wordy && (short || upper)) {
      const flags = stopwordAcronym ? "" : "i";
      out.push(new RegExp(`\\b${escapeRegex(part)}\\b`, flags));
    } else {
      out.push(new RegExp(escapeRegex(part), "i"));
    }
  }
  return out;
}

const TERM_PATTERN_CACHE = new Map<string, RegExp[]>();

export function patternsFor(term: string): RegExp[] {
  let cached = TERM_PATTERN_CACHE.get(term);
  if (!cached) {
    cached = termPatterns(term);
    TERM_PATTERN_CACHE.set(term, cached);
  }
  return cached;
}

export function termsForText(allTerms: ReadonlyArray<string>, text: string): Set<string> {
  const hit = new Set<string>();
  for (const term of allTerms) {
    const patterns = patternsFor(term);
    if (patterns.some((re) => re.test(text))) hit.add(term);
  }
  return hit;
}

export const cssEscape = (s: string): string =>
  typeof window !== "undefined" && window.CSS && typeof window.CSS.escape === "function"
    ? window.CSS.escape(s)
    : s.replace(/["\\]/g, "\\$&");
